import { GridFiltersController } from "@repo/baseCloud";
import { holdInUtc, holdPropsInUtc, PushNotification } from "@repo/helpers";
import { KeyedArray } from "@repo/models";
import { useCallback } from "react";

import { useRepContext } from "../contexts/editableItemContext";
import MindflashCourse from "../models/mindflashCourse";
import MindflashCustomField from "../models/mindflashCustomField";
import { ITrainerCertification, MVCStateSpecificTraining } from "../models/MVCStateSpecificTraining";
import Rep from "../models/rep";
import { RepTraining } from "../models/repSegment";
import * as Queries from "./queries";
import { useQueryWrapper, useRepPortalApi } from "./repPortalApi";

export function useTrainingApi() {
	const { Get, Post } = useRepPortalApi();

	const saveRepTraining = useCallback(
		function (repId: number, repTraining: RepTraining) {
			const params = {
				repId,
			};
			return Post<boolean>("SaveTrainingInformation", params, repTraining);
		},
		[Post],
	);

	const loadRepTraining = useCallback(
		function (repId: number) {
			return Get<Rep>("GetRepresentativeInfo", { repId });
		},
		[Get],
	);

	const getMindflashCourses = useCallback(
		function (repId: number) {
			return Get<MindflashCourse[]>("Trainings", { repId });
		},
		[Get],
	);

	const loadMindflashCourses = useCallback(
		function (repId: number) {
			return async function () {
				if (!repId) return defaultMindflashCourseArr();
				try {
					const courses = await getMindflashCourses(repId);
					const keyedItems = new KeyedArray<MindflashCourse>(courses, (b) => b.courseId);
					return keyedItems;
				} catch (e) {
					PushNotification("Unable to retrieve background checks");
					return defaultMindflashCourseArr();
				}
			};
		},
		[getMindflashCourses],
	);

	const initialize = useCallback(function (training: MVCStateSpecificTraining) {
		training.trainingDate = holdInUtc(training.trainingDate);
		return training;
	}, []);

	const getStateSpecificTraining = useCallback(
		function (repId: number, trainingId: number) {
			return Get<MVCStateSpecificTraining>("GetStateSpecificTraining", {
				repId,
				trainingId,
			}).then(initialize);
		},
		[Get, initialize],
	);

	const getStateSpecificTrainings = useCallback(
		async function (repId: number) {
			const trainings = await Get<MVCStateSpecificTraining[]>("getStateSpecificTrainings", { repId });
			return trainings.map(initialize);
		},
		[Get, initialize],
	);

	const loadStateSpecificTrainings = useCallback(
		function (repId: number) {
			return async function () {
				if (!repId) return defaultStateSpecificTrainingArr();
				try {
					const courses = await getStateSpecificTrainings(repId);
					const keyedItems = new KeyedArray<MVCStateSpecificTraining>(courses, (b) => b.id);
					return keyedItems;
				} catch (e) {
					PushNotification("Unable to retrieve state specific trainings");
					return defaultStateSpecificTrainingArr();
				}
			};
		},
		[getStateSpecificTrainings],
	);

	const deleteStateSpecificTraining = useCallback(function (id: number) {
		return Post("DeleteStateSpecificTraining", { id });
	}, []);

	const initializeTrainerCert = useCallback(function (training: ITrainerCertification) {
		holdPropsInUtc(training, "phase1", "phase2", "phase3", "master");
		return training;
	}, []);

	const getTrainerCertification = useCallback(
		function (trainingId: number) {
			return Get<ITrainerCertification>("GetTrainerCertification", {
				trainingId,
			}).then(initializeTrainerCert);
		},
		[Get, initializeTrainerCert],
	);
	const getTrainerCertifications = useCallback(
		async function (repId: number) {
			const trainings = await Get<ITrainerCertification[]>("GetTrainerCertifications", { repId });
			return trainings.map(initializeTrainerCert);
		},
		[Get, initializeTrainerCert],
	);

	const loadTrainerCertifications = useCallback(
		function (repId: number) {
			return async function () {
				if (!repId) return defaultTrainerCertificationArr();
				try {
					const courses = await getTrainerCertifications(repId);
					const keyedItems = new KeyedArray<ITrainerCertification>(courses, (b) => b.id);
					return keyedItems;
				} catch (e) {
					PushNotification("Unable to retrieve state specific trainings");
					return defaultTrainerCertificationArr();
				}
			};
		},
		[getTrainerCertifications],
	);

	const deleteTrainerCertification = useCallback(function (id: number) {
		return Post("DeleteTrainerCertification", { id });
	}, []);

	const saveTrainerCertification = useCallback(
		function (repId: number, training: ITrainerCertification) {
			training = { ...training, representativeId: repId };
			return Post<number, ITrainerCertification>("SaveTrainerCertification", {}, training);
		},
		[Post],
	);

	const saveRepAdditionalTrainings = useCallback(
		function (repId: number, training: MVCStateSpecificTraining) {
			const params = { repId };
			return Post<number, MVCStateSpecificTraining>("SaveStateSpecificTraining", params, training);
		},
		[Post],
	);

	const loadMindflashCustomFields = useCallback(
		(repId: number) => async () => Get<MindflashCustomField[]>("MindflashCustomFields", { repId }),
		[Get],
	);

	const saveMindflashCustomFields = useCallback(
		function (repId: number, fields: MindflashCustomField[]) {
			return Post<boolean>("SaveMindflashCustomFields", null, {
				repId,
				fields,
			});
		},
		[Post],
	);

	const createTrakStarUser = useCallback(
		function (repId: number) {
			return Post<boolean>("ConnectToMindflash", { repId });
		},
		[Get],
	);

	const getAllTrainingCourses = useCallback(
		() =>
			async function () {
				const allTrainingCourses = await Get<MindflashCourse[]>("GetAllTrainingCourses");
				return allTrainingCourses.map((item) => ({
					value: item.courseId,
					label: item.courseName,
				}));
			},
		[Get],
	);

	const assignTrainingCourse = useCallback(
		function (repId: number, courseId: string, required: boolean) {
			return Post<boolean>("AssignTrainingCourse", {
				repId,
				courseId,
				required,
			});
		},
		[Get],
	);

	return {
		saveRepTraining,
		loadRepTraining,
		loadMindflashCourses,
		getStateSpecificTraining,
		loadStateSpecificTrainings,
		saveRepAdditionalTrainings,
		deleteStateSpecificTraining,
		loadMindflashCustomFields,
		saveMindflashCustomFields,
		createTrakStarUser,
		getAllTrainingCourses,
		assignTrainingCourse,
		getTrainerCertification,
		loadTrainerCertifications,
		saveTrainerCertification,
		deleteTrainerCertification,
	};
}

export function useTrainingQueries() {
	const { repId } = useRepContext();
	const {
		loadMindflashCourses,
		loadMindflashCustomFields,
		getAllTrainingCourses,
		loadStateSpecificTrainings,
		loadTrainerCertifications,
	} = useTrainingApi();

	const useMindFlashQuery = () => useQueryWrapper(Queries.MIND_FLASH, [repId], loadMindflashCourses(repId));

	const useMindflashCustomFieldsQuery = () =>
		useQueryWrapper(Queries.MIND_FLASH_CUSTOM_FIELDS, [repId], loadMindflashCustomFields(repId));

	const useAllMindflashTrainingsQuery = () =>
		useQueryWrapper(Queries.MIND_FLASH_ALL_TRAININGS, [repId], getAllTrainingCourses());

	const useStateSpecificTrainingsQuery = () =>
		useQueryWrapper(Queries.STATE_SPECIFIC_TRAININGS, [repId], loadStateSpecificTrainings(repId));

	const useTrainerCertificationsQuery = () =>
		useQueryWrapper(Queries.TRAINER_CERTIFICATIONS, [repId], loadTrainerCertifications(repId));

	const useStatesQuery = () => useQueryWrapper([Queries.STATES], [repId], () => GridFiltersController.getStates());

	return {
		useMindFlashQuery,
		useMindflashCustomFieldsQuery,
		useAllMindflashTrainingsQuery,
		useStateSpecificTrainingsQuery,
		useStatesQuery,
		useTrainerCertificationsQuery,
	};
}

const defaultMindflashCourseArr = () => new KeyedArray<MindflashCourse>([], (b) => b.courseId);
const defaultStateSpecificTrainingArr = () => new KeyedArray<MVCStateSpecificTraining>([], (b) => b.id);
const defaultTrainerCertificationArr = () => new KeyedArray<ITrainerCertification>([], (b) => b.id);
